import { useContext, useEffect, useState } from 'react';
import { IconButton } from '@upmc/ims.component-library';
import MoreIcon from '@mui/icons-material/MoreVert';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import { CONTENT_ALIGNMENT } from '../../UI/MDC/Tooltip/_types';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Logo from './Logo';
import { MSPerson } from './MSPerson/MSPerson';
import NotificationIcon from './NotificationIcon';
import BackToHomeIcon from './BackToHomeIcon';
import { ImpersonationIcon } from './ImpersonationIcon/ImpersonationIcon';
import { GraphAxios as axiosInstance } from '../../../services/axiosGraph/axios';
import { UserContext } from 'context/User/UserContext';
import LogoutButton from './LogoutButton';
import AutoCompleteInputSearch from './AutocompleteInputSearch';

/**
 * App bar to be displayed at top of screen.
 * Contains logo, search bar, and icons.
 * @returns TopAppBar component
 */
const TopAppBar = () => {
	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
    const contextUser = useContext(UserContext);
	const [anchorEl, setAnchorEl] = useState(null);
	const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = useState(null);

	const isDEVTeam = contextUser.userInfo.userProfile.isDEVTeam;
	const isMenuOpen = Boolean(anchorEl);
	const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

	const handleProfileMenuOpen = event => {
		setAnchorEl(event.currentTarget);
	};

	const handleMobileMenuClose = () => {
		setMobileMoreAnchorEl(null);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
		handleMobileMenuClose();
	};

	const handleMobileMenuOpen = event => {
		setMobileMoreAnchorEl(event.currentTarget);
	};

	const [mgtAccount, setMgtAccount] = useState(null);

	useEffect(() => {
		axiosInstance.get('/me').then(x => {
			setMgtAccount(x.data);
		});
	}, []);

	const menuId = 'primary-search-account-menu';
	const renderProfileMenu = (
		<Menu
			anchorEl={anchorEl}
			anchorOrigin={{
				vertical: 'bottom',
				horizontal: 'right',
			}}
			id={menuId}
			keepMounted
			transformOrigin={{
				vertical: 'top',
				horizontal: 'right',
			}}
			open={isMenuOpen}
			onClose={handleMenuClose}
		>
			<LogoutButton />
		</Menu>
	);

	const mobileMenuId = 'primary-search-account-menu-mobile';
	const renderMobileMenu = (
		<Menu
			anchorEl={mobileMoreAnchorEl}
			anchorOrigin={{
				vertical: 'top',
				horizontal: 'right',
			}}
			id={mobileMenuId}
			keepMounted
			transformOrigin={{
				vertical: 'top',
				horizontal: 'right',
			}}
			open={isMobileMenuOpen}
			onClose={handleMobileMenuClose}
		>
			<MenuItem>
				<ListItemIcon>
					<BackToHomeIcon align={CONTENT_ALIGNMENT.LEFT} />
				</ListItemIcon>
			</MenuItem>
			{isDEVTeam && (
				<MenuItem>
					<ListItemIcon>
						<ImpersonationIcon align={CONTENT_ALIGNMENT.LEFT} />
					</ListItemIcon>
				</MenuItem>
			)}
			<MenuItem>
				<ListItemIcon>
					<LogoutButton isIcon align={CONTENT_ALIGNMENT.LEFT} />
				</ListItemIcon>
			</MenuItem>
		</Menu>
	);

	const environmentIndicator = screenSize => {
		let environment = 'Unknown';
		switch (process.env.REACT_APP_IMS_ENV?.toLowerCase()) {
			case 'development':
				environment = 'Development';
				break;
			case 'demo':
				environment = 'Demo';
				break;
			case 'test':
				environment = 'Test';
				break;
			default:
				return null;
		}

		if (screenSize === 'small') {
			environment =
				environment.length > 4 ? environment.slice(0, 3) : environment;
		}

		return (
			<Typography variant='h6' color='red' component='div'>
				{environment}
			</Typography>
		);
	};

	return (
		<Box sx={{ flexGrow: 1 }}>
			<AppBar
				position='fixed'
				sx={{ zIndex: theme => theme.zIndex.drawer + 1 }}
			>
				<Toolbar>
					<Box sx={{ mr: 2 }}>
						<Logo inverted />
					</Box>
					<Box
						sx={{
							display: { xs: 'none', md: 'none', lg: 'block' },
						}}
					>
						<Typography
							variant='h6'
							component='div'
							sx={{ overflowY: 'hidden', wordWrap: 'none' }}
						>
							Identity Management System
						</Typography>
						{environmentIndicator()}
					</Box>
					<Box
						sx={{
							display: { xs: 'block', md: 'block', lg: 'none' },
						}}
					>
						<Typography
							variant='h6'
							component='div'
							sx={{ overflowY: 'hidden', wordWrap: 'none' }}
						>
							IMS
						</Typography>
						{environmentIndicator('small')}
					</Box>
					<Box sx={{ flexGrow: 1 }} />
					<AutoCompleteInputSearch />
					<Box
						sx={{
							display: { xs: 'none', md: 'flex' },
							alignItems: { md: 'center' },
						}}
					>
						<BackToHomeIcon />
						<NotificationIcon />
						{isDEVTeam && <ImpersonationIcon />}
						{mgtAccount && (
							<IconButton
								size='large'
								edge='end'
								aria-label='account of current user'
								aria-controls={menuId}
								aria-haspopup='true'
								onClick={handleProfileMenuOpen}
								color='inherit'
							>
								<MSPerson person={mgtAccount} />
							</IconButton>
						)}
					</Box>
					<Box sx={{ display: { xs: 'flex', md: 'none' } }}>
						<IconButton
							size='large'
							aria-label='show more'
							aria-controls={mobileMenuId}
							aria-haspopup='true'
							onClick={handleMobileMenuOpen}
							color='inherit'
						>
							<MoreIcon />
						</IconButton>
					</Box>
				</Toolbar>
			</AppBar>
			{renderMobileMenu}
			{renderProfileMenu}
		</Box>
	);
};

export default TopAppBar;
